/**
 * React Query keys for the Departments module, and the one place that invalidates them.
 *
 * Other modules read ['departments', 'views'] / ['departments', 'list-view'] (getAllModelViews),
 * the bare ['departments'] and ['ministries'] prefixes, so every write invalidates those prefixes.
 */

import type { QueryClient } from '@tanstack/react-query';

export const departmentsKeys = {
  root: ['departments'] as const,
  /** DepartmentModelView[] from getAllModelViews(). Shared with Tasks, Budgets, Events, Forms. */
  views: ['departments', 'views'] as const,
  detail: (id: number) => ['departments', 'detail', id] as const,
  /** MemberInDepartmentModelView[] of one department (active only). */
  members: (id: number) => ['departments', 'members', id] as const,
  /** Every department membership (the Department members page). */
  allMembers: ['departments', 'members', 'all'] as const,
  /** Designation[] of one department. */
  designations: (id: number) => ['departments', 'designations', id] as const,
  /** Every designation. */
  allDesignations: ['departments', 'designations', 'all'] as const,
  designationMembers: (designationId: number) => ['departments', 'designation-members', designationId] as const,
  designationTasks: (designationId: number) => ['departments', 'designation-tasks', designationId] as const,
};

export const ministriesKeys = {
  root: ['ministries'] as const,
  /** MinistryModelView[] from getAllMinistryViews(). */
  views: ['ministries', 'views'] as const,
  detail: (id: number) => ['ministries', 'detail', id] as const,
  members: (id: number) => ['ministries', 'members', id] as const,
  /** Every ministry's members (the Ministry members page). */
  allMembers: ['ministries', 'members', 'all'] as const,
  roles: ['ministries', 'roles'] as const,
  teams: (id: number | null) => ['ministries', 'teams', id ?? 'all'] as const,
  equipment: (id: number | null) => ['ministries', 'equipment', id ?? 'all'] as const,
  activities: (id: number | null) => ['ministries', 'activities', id ?? 'all'] as const,
};

/** Member search for the "Add a member" picker (another module owns the 'members' prefix). */
export const memberSearchKey = (term: string) => ['department-member-search', term] as const;

/** Call after every department / designation / membership write. */
export function invalidateDepartmentQueries(queryClient: QueryClient) {
  // Prefix match: every key above plus the bare ['departments'] and ['departments', 'list-view'] keys other modules use.
  queryClient.invalidateQueries({ queryKey: ['departments'] });
  // Task create wizard caches designations and designation tasks under its own keys.
  queryClient.invalidateQueries({ queryKey: ['designations'] });
  queryClient.invalidateQueries({ queryKey: ['designation-tasks'] });
  // Member profile "Ministries and roles" tab.
  queryClient.invalidateQueries({ queryKey: ['member-departments'] });
}

/** Call after every ministry / role / team / equipment / activity / membership write. */
export function invalidateMinistryQueries(queryClient: QueryClient) {
  queryClient.invalidateQueries({ queryKey: ['ministries'] });
  queryClient.invalidateQueries({ queryKey: ['ministry-roles'] });
  queryClient.invalidateQueries({ queryKey: ['member-ministries'] });
}
